/**
 * A chat that has just started can have its first message (typically the widget
 * auto-welcome) arrive over the socket before the room has a row in the chat
 * list. The list update has nothing to write to and the text is dropped, so the
 * row shows up with a blank preview until a second message arrives.
 *
 * These helpers hold that first message just long enough for the row to be
 * inserted — by the socket room payload or by a background refetch — so it can
 * be used as a fallback preview.
 */

/** How long an unplaced preview is worth keeping. */
export const PENDING_ROOM_PREVIEW_TTL_MS = 60_000;

/**
 * Fired when a preview is recovered from a source the chat list cannot see, so
 * the list can fill a row it had to render blank.
 */
export const ROOM_PREVIEW_RESOLVED_EVENT = "chat-room-preview-resolved";

const pendingPreviewByRoomId = new Map();

/**
 * @param {string | null | undefined} roomId
 * @param {{ description?: string, attachment?: boolean, iso?: string | null }} preview
 */
export function rememberPendingRoomPreview(roomId, preview) {
  if (!roomId || !preview) return;
  const description =
    preview.description != null ? String(preview.description).trim() : "";
  const attachment = Boolean(preview.attachment);
  if (!description && !attachment) return;

  pendingPreviewByRoomId.set(String(roomId), {
    description,
    attachment,
    iso: preview.iso || null,
    expiresAt: Date.now() + PENDING_ROOM_PREVIEW_TTL_MS,
  });
}

/**
 * @param {string | null | undefined} roomId
 * @returns {{ description: string, attachment: boolean, iso: string | null } | null}
 */
export function getPendingRoomPreview(roomId) {
  if (!roomId) return null;
  const key = String(roomId);
  const entry = pendingPreviewByRoomId.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    pendingPreviewByRoomId.delete(key);
    return null;
  }
  return {
    description: entry.description,
    attachment: entry.attachment,
    iso: entry.iso,
  };
}

/**
 * Remember a preview and tell the chat list about it. Used when the opened
 * thread turns out to hold a message the room list API did not report.
 *
 * @param {string | null | undefined} roomId
 * @param {{ description?: string, attachment?: boolean, iso?: string | null }} preview
 */
export function publishRoomPreview(roomId, preview) {
  rememberPendingRoomPreview(roomId, preview);
  const stored = getPendingRoomPreview(roomId);
  if (!stored) return;
  if (typeof window === "undefined" || !window.dispatchEvent) return;

  window.dispatchEvent(
    new CustomEvent(ROOM_PREVIEW_RESOLVED_EVENT, {
      detail: { roomId: String(roomId), ...stored },
    })
  );
}

/**
 * @param {string | null | undefined} roomId
 */
export function clearPendingRoomPreview(roomId) {
  if (!roomId) return;
  pendingPreviewByRoomId.delete(String(roomId));
}

/** @internal Test helper */
export function clearAllPendingRoomPreviews() {
  pendingPreviewByRoomId.clear();
}
